import React, { useCallback, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Field, FieldArray, reduxForm, change, getFormSyncErrors } from "redux-form";
import {
    renderField,
    renderFieldCheck,
    renderNumber,
    SelectField,
    renderFilePicker,
    AsyncSelectField,
} from "../../Utils/renderField/renderField";
import {
    combine,
    validate,
    validatorFromFunction,
    validators,
} from "validate-redux-form";
import {
    roles,
    licenceType,
    USER_RANGES,
    ROLE_ADMIN,
    FIREFIGHTER_TYPE,
    USER_STATUS,
    INACTIVE_STATUS,
    CONTRACT,
    GENDER,
} from "../../../../utility/constants";
import { Link } from "react-router-dom";
import { api } from "api";
import LoadMask from "../../Utils/LoadMask/LoadMask";
import ApprovedCourses from "./ApprovedCourses";
import CustomPermissionsSection from "./CustomPermissionsSection";
import { validateDPI, validateNumber, validateWhiteSpace } from "../../../../utility/validation";

const onlyNumbers = value => {
    if (!value) {
        return value
    }
    return value.replace(/[^\d]/g, '')
}

const loadStations = (search) => {
    let params = {};
    if (search) params.search = search;
    return api
        .get("station", params)
        .then((response) => {
            return response.results;
        })
        .catch((error) => {
            console.error(error);
        });
};

const loadJobManagement = (search) => {
    let params = {};
    if (search) params.search = search;
    return api
        .get("job_management", params)
        .then((response) => {
            return response.results;
        })
        .catch((error) => {
            console.error(error);
        });
};

// loadRoleConfigurations is now built dynamically inside the Form component
// to filter by the currently selected primary role.

const Form = (props) => {
    const {
        handleSubmit,
        isView,
        isUpdate,
        setAvatar,
        item,
        loading,
        valueIsPilot,
        list,
        updateList,
        isCreate = false,
        showInactive,
        setShowInactive,
        me = {},
        url = "",
    } = props;

    const status_firefighter =
        item && item.status && item.status === "Activo"
            ? { label: "Activo", value: 5 }
            : item.status === "Inactivo"
                ? { label: "Inactivo", value: 1 }
                : {};
    const [status, setStatus] = useState(status_firefighter);
    const [systemRolesList, setSystemRolesList] = useState([]);

    // Leer el rol principal seleccionado actualmente desde el estado de Redux Form
    const selectedRole = useSelector((state) => {
        const formValues = state.form && state.form.userForm && state.form.userForm.values;
        return formValues ? formValues.role : null;
    });

    // loadOptions dinámico que filtra por el rol principal seleccionado
    const loadRoleConfigurations = useCallback((search) => {
        let params = {};
        if (search) params.search = search;
        // Filtrar por el rol principal seleccionado para mostrar solo sus perfiles
        if (selectedRole !== null && selectedRole !== undefined) {
            params.role = selectedRole;
        }
        return api
            .get("roles", params)
            .then((response) => {
                return Array.isArray(response) ? response : (response.results || []);
            })
            .catch((error) => {
                console.error(error);
                return [];
            });
    }, [selectedRole]);

    // Estado para permisos personalizados
    const [customPermissions, setCustomPermissions] = useState((item && item.custom_permissions) || {});
    const [basePermissions, setBasePermissions] = useState((item && item.base_permissions) || {});

    // Actualizar basePermissions cuando cambia el selectedRole
    // Esto garantiza que CustomPermissionsSection siempre muestre los permisos base correctos
    useEffect(() => {
        if (selectedRole === null || selectedRole === undefined) return;
        // Fetch la configuración base del rol seleccionado
        api.get('roles', { role: selectedRole, page_size: 100 })
            .then((response) => {
                const configs = Array.isArray(response) ? response : (response.results || []);
                // Buscar la configuración marcada como base (order_by id → la primera es la base)
                if (configs.length > 0) {
                    const baseConfig = configs[0]; // la primera = configuración base por defecto
                    setBasePermissions(baseConfig.permissions || {});
                } else {
                    // Si no hay ninguna configuración para este rol, vaciar basePermissions
                    setBasePermissions({});
                }
            })
            .catch(() => {
                setBasePermissions({});
            });
    }, [selectedRole]);

    useEffect(() => {
        // Cargar roles del sistema
        api.get("system-roles", { page_size: 1000 })
            .then((response) => {
                const data = Array.isArray(response) ? response : (response.results || []);
                const list = data
                        .filter(role => role.active)
                        .map(role => ({
                            label: role.name,
                            value: role.const_value, 
                            const_value: role.const_value,
                            original_id: role.id
                        }));
                setSystemRolesList(list);

                // Re-dispatch the form role value after options load
                // so the SelectField renders the pre-selected value correctly
                if (item && item.role !== undefined && item.role !== null && props.dispatch) {
                    const numericRole = typeof item.role === 'object' ? item.role.role : item.role;
                    props.dispatch(change('userForm', 'role', numericRole));
                }
            })
            .catch(() => { });

        setShowInactive(
            item && item.status && item.status === "Activo"
                ? false
                : item.status === "Inactivo"
                    ? true
                    : false
        );
    }, []);

    // Sincronizar customPermissions con redux-form
    useEffect(() => {
        if (props.dispatch) {
            props.dispatch(change('userForm', 'custom_permissions', customPermissions));
        }
    }, [customPermissions]);



    // Limpiar No. de Registro si el rango es 0 (Sin rango)
    useEffect(() => {
        if (props.user_range_value === 0 && props.dispatch) {
            props.dispatch(change('userForm', 'no_registry', ''));
        }
    }, [props.user_range_value]);

    const validateChange = (e) => {
        const value =
            e && e === 5
                ? { label: "Activo", value: 5 }
                : e === 1
                    ? { label: "Inactivo", value: 1 }
                    : {};

        e && e === 5
            ? setShowInactive(false)
            : e === 1
                ? setShowInactive(true)
                : setShowInactive(false);
        setStatus(value);
    };

    const [sections, setSections] = useState({
        general: true,
        contact: false,
        account: false,
        courses: false,
    });

    const generalRef = useRef(null);
    const contactRef = useRef(null);
    const accountRef = useRef(null);
    const coursesRef = useRef(null);

    const syncErrors = useSelector((state) => getFormSyncErrors("userForm")(state));
    const { submitFailed } = props;

    const toggleSection = (section) => {
        setSections({
            ...sections,
            [section]: !sections[section],
        });
    };

    useEffect(() => {
        if (submitFailed && syncErrors) {
            const errorFields = Object.keys(syncErrors);
            let newSections = { ...sections };
            let firstErrorSection = null;

            const generalFields = ["no_dpi", "first_name", "last_name", "email", "date_of_birth", "photo"];
            const contactFields = ["phone_number", "address", "gender", "station"];
            const accountFields = ["role", "role_configuration", "user_range", "no_registry", "firefighter_type", "is_pilot", "no_license", "type_license", "job_management", "status", "inactive_reason", "start_date_unsubscribe", "end_date_unsubscribe", "type_of_contract", "password", "confirm_password"];
            const coursesFields = ["other"];

            if (generalFields.some(f => errorFields.includes(f))) {
                newSections.general = true;
                if (!firstErrorSection) firstErrorSection = generalRef;
            }
            if (contactFields.some(f => errorFields.includes(f))) {
                newSections.contact = true;
                if (!firstErrorSection) firstErrorSection = contactRef;
            }
            if (accountFields.some(f => errorFields.includes(f))) {
                newSections.account = true;
                if (!firstErrorSection) firstErrorSection = accountRef;
            }
            if (coursesFields.some(f => errorFields.includes(f))) {
                newSections.courses = true;
                if (!firstErrorSection) firstErrorSection = coursesRef;
            }

            setSections(newSections);

            if (firstErrorSection && firstErrorSection.current) {
                firstErrorSection.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [submitFailed, syncErrors]);

    const renderAccordionHeader = (title, sectionKey, ref) => (
        <div
            ref={ref}
            className="d-flex justify-content-between align-items-center p-3 mb-2 bg-light border rounded cursor-pointer"
            onClick={() => toggleSection(sectionKey)}
            style={{ cursor: 'pointer' }}
        >
            <h4 className="m-0">{title}</h4>
            <i className={`fa fa-chevron-${sections[sectionKey] ? 'up' : 'down'}`}></i>
        </div>
    );

    return (
        <form onSubmit={handleSubmit}>
            <LoadMask loading={loading} dark blur>
                {renderAccordionHeader("Datos Generales", "general", generalRef)}
                <div
                    style={{
                        maxHeight: sections.general ? '2000px' : '0',
                        overflow: sections.general ? 'visible' : 'hidden',
                        transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out',
                        opacity: sections.general ? 1 : 0
                    }}
                >
                    <div className="d-flex flex-wrap border p-3 mb-3 rounded">
                        <div className="d-flex flex-column flex-1 mx-3">
                            <div className="form-group has-feedback">
                                <label> No. de DPI <span className="text-danger">*</span></label>
                                <Field
                                    name="no_dpi"
                                    disabled={isView}
                                    component={renderNumber}
                                    className="m-2"
                                    numberFormat={"#### ##### ####"}
                                    validate={validateDPI}
                                />
                            </div>
                            <div className="form-group has-feedback">
                                <label> Nombres <span className="text-danger">*</span></label>
                                <Field
                                    name="first_name"
                                    component={renderField}
                                    disabled={isView}
                                    className="m-2"
                                    type="text"
                                    validate={validateWhiteSpace}
                                />
                            </div>
                            <div className="form-group has-feedback">
                                <label> Apellidos <span className="text-danger">*</span></label>
                                <Field
                                    name="last_name"
                                    component={renderField}
                                    disabled={isView}
                                    className="m-2"
                                    type="text"
                                    validate={validateWhiteSpace}
                                />
                            </div>

                            <div className="form-group has-feedback">
                                <label>Correo Electronico <span className="text-danger">*</span></label>
                                <Field
                                    name="email"
                                    component={renderField}
                                    disabled={isView}
                                    className="m-2"
                                    type="email"
                                />
                            </div>

                            <div className="form-group has-feedback">
                                <label>Fecha de Nacimiento</label>
                                <Field
                                    name="date_of_birth"
                                    label="date_of_birth"
                                    component={renderField}
                                    disabled={isView}
                                    type={"date"}
                                    className="form-control"
                                />
                            </div>
                        </div>
                        <div className="col-12 col-md-6 form-group">
                            <label>Fotografia</label>
                            <Field
                                photo={item.photo ? item.photo : null}
                                name="photo"
                                label={"Subir imagen"}
                                disabled={isView}
                                setFile={setAvatar}
                                component={renderFilePicker}
                            />
                        </div>
                    </div>
                </div>

                {renderAccordionHeader("Información de contacto", "contact", contactRef)}
                <div
                    style={{
                        maxHeight: sections.contact ? '2000px' : '0',
                        overflow: sections.contact ? 'visible' : 'hidden',
                        transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out',
                        opacity: sections.contact ? 1 : 0
                    }}
                >
                    <div className="d-flex flex-wrap border p-3 mb-3 rounded">
                        <div className="col-12 col-md-6 form-group">
                            <label>Número de Teléfono <span className="text-danger">*</span></label>
                            <Field
                                name="phone_number"
                                disabled={isView}
                                component={renderNumber}
                                numberFormat={"+(502) ####-####"}
                                className="m-2"
                                validate={validateNumber}
                            />
                        </div>
                        <div className="col-12 col-md-6 form-group">
                            <label>Dirección <span className="text-danger">*</span></label>
                            <Field
                                name="address"
                                disabled={isView}
                                component={renderField}
                                className="m-2"
                                type="text"
                                validate={validateWhiteSpace}
                            />
                        </div>
                        <div className="col-12 col-md-6 form-group">
                            <label> Sexo </label>
                            <Field
                                name="gender"
                                disabled={isView}
                                options={GENDER}
                                component={SelectField}
                                className="m-2"
                            />
                        </div>
                        <div className="col-12 col-md-6 form-group">
                            <label>Estación <span className="text-danger">*</span></label>
                            <Field
                                name="station"
                                component={AsyncSelectField}
                                disabled={isView}
                                loadOptions={loadStations}
                                getOptionValue={(option) => option.id}
                                getOptionLabel={(option) => option.name}
                                className="m-2"
                                type="text"
                            />
                        </div>
                    </div>
                </div>

                {renderAccordionHeader("Información de la cuenta", "account", accountRef)}
                <div
                    style={{
                        maxHeight: sections.account ? '3000px' : '0',
                        overflow: sections.account ? 'visible' : 'hidden',
                        transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out',
                        opacity: sections.account ? 1 : 0
                    }}
                >
                    <div className="d-flex flex-wrap border p-3 mb-3 rounded">
                        <div className="col-12 col-md-6 form-group">
                            <label> Rol del Sistema <span className="text-danger">*</span></label>
                            <Field
                                name="role"
                                component={SelectField}
                                options={systemRolesList}
                                disabled={isView}
                                className="m-2"
                                onChange={(e) => {
                                    // Limpiar role_configuration cuando cambia el rol principal
                                    // para evitar que el perfil anterior anule los permisos del nuevo rol
                                    if (props.dispatch) {
                                        props.dispatch(change('userForm', 'role_configuration', null));
                                    }
                                }}
                            />

                            <small className="form-text text-muted mx-2">
                                Selecciona el rol base del sistema para este usuario.
                            </small>
                        </div>
                        {/* Oculto: El perfil se asigna automáticamente en el backend basado en el rol principal */}
                        <div style={{ display: 'none' }}>
                            <Field
                                key={selectedRole}
                                name="role_configuration"
                                component={AsyncSelectField}
                                disabled={isView}
                                loadOptions={loadRoleConfigurations}
                                isClearable={true}
                                getOptionValue={(option) => option.id}
                                getOptionLabel={(option) => option.name || option.role_display}
                                className="m-2"
                                type="text"
                                customChange={(profile) => {
                                    if (profile && profile.role) {
                                        props.change('userForm', 'role', profile.role);
                                    }
                                }}
                            />
                        </div>
                        <div className="col-12 col-md-6 form-group">
                            <label> Rango <span className="text-danger">*</span></label>
                            <Field
                                className="m-2"
                                name="user_range"
                                disabled={isView}
                                options={USER_RANGES}
                                component={SelectField}
                            />
                        </div>
                        <div className="col-12 col-md-6 form-group">
                            <label> No. de Registro {props.user_range_value !== 0 && <span className="text-danger">*</span>}</label>
                            <Field
                                type="text"
                                name="no_registry"
                                disabled={isView || props.user_range_value === 0}
                                component={renderField}
                                className="m-2"
                                normalize={onlyNumbers}
                            />
                            {props.user_range_value === 0 && (
                                <small className="form-text text-muted mx-2">
                                    Campo deshabilitado para usuarios sin rango
                                </small>
                            )}
                        </div>
                        <div className="col-12 col-md-6 form-group">
                            <label> Tipo Bombero </label>
                            <Field
                                className="m-2"
                                name="firefighter_type"
                                disabled={isView}
                                options={FIREFIGHTER_TYPE}
                                component={SelectField}
                            />
                        </div>
                        <div className="col-12 col-md-6 form-group mt-4">
                            <Field
                                name="is_pilot"
                                disabled={isView}
                                component={renderFieldCheck}
                                label="Es piloto"
                                className="m-2"
                            />
                        </div>
                        {valueIsPilot === true && (
                            <React.Fragment>
                                <div className="col-12 col-md-6 form-group">
                                    <label> No. de licencia <span className="text-danger">*</span></label>
                                    <Field
                                        name="no_license"
                                        disabled={isView}
                                        component={renderField}
                                        className="m-2"
                                        type="number"
                                    />
                                </div>
                                <div className="col-12 col-md-6 form-group">
                                    <label> Tipo de licencia <span className="text-danger">*</span></label>
                                    <Field
                                        name="type_license"
                                        disabled={isView}
                                        component={SelectField}
                                        options={licenceType}
                                        className="m-2"
                                    />
                                </div>
                            </React.Fragment>
                        )}
                        <div className="col-12 col-md-6 form-group">
                            <label>Puesto <span className="text-danger">*</span></label>
                            <Field
                                name="job_management"
                                component={AsyncSelectField}
                                disabled={isView}
                                loadOptions={loadJobManagement}
                                getOptionValue={(option) => option.id}
                                getOptionLabel={(option) => option.job_title}
                                className="m-2"
                                type="text"
                            />
                        </div>
                        {!isCreate && (
                            <React.Fragment>
                                <div className="col-12 col-md-6 form-group">
                                    <label> Estado del Bombero </label>
                                    <Field
                                        className="m-2"
                                        name="status"
                                        disabled={true}
                                        options={USER_STATUS}
                                        component={SelectField}
                                        valueSelect={status}
                                        parse={(e) => validateChange(e)}
                                    />
                                </div>
                                {showInactive && (
                                    <div className="col-12 col-md-6 form-group">
                                        <label> Razón Inactivo </label>
                                        <Field
                                            className="m-2"
                                            name="inactive_reason"
                                            disabled={true}
                                            options={INACTIVE_STATUS}
                                            component={SelectField}
                                        />
                                    </div>
                                )}
                            </React.Fragment>
                        )}

                        {isView ? (
                            showInactive ? (
                                <React.Fragment>
                                    <div className="col-12 col-md-6 form-group">
                                        <label>Fecha inicio de baja</label>
                                        <Field
                                            name="start_date_unsubscribe"
                                            label="start_date_unsubscribe"
                                            component={renderField}
                                            disabled={isView}
                                            type={"date"}
                                            className="form-control"
                                        />
                                    </div>
                                    <div className="col-12 col-md-6 form-group">
                                        <label>Fecha final de baja</label>
                                        <Field
                                            name="end_date_unsubscribe"
                                            label="end_date_unsubscribe"
                                            component={renderField}
                                            disabled={isView}
                                            type={"date"}
                                            className="form-control"
                                        />
                                    </div>

                                    <div className="col-12 col-md-6 form-group">
                                        <label>Archivo de registro</label>
                                        <div>
                                            {item.register_file ? (
                                                <a
                                                    href={
                                                        item.register_file &&
                                                        item.register_file
                                                    }
                                                    target="_blank"
                                                >
                                                    {item.register_file.replace(
                                                        "/media/docs/",
                                                        ""
                                                    )}
                                                </a>
                                            ) : (
                                                <h5>Sin Archivo de registro</h5>
                                            )}
                                        </div>
                                    </div>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <div className="col-12 col-md-6 form-group">
                                        <label> Tipo de contratación </label>
                                        <Field
                                            className="m-2"
                                            name="type_of_contract"
                                            disabled={true}
                                            options={CONTRACT}
                                            component={SelectField}
                                            // valueSelect={status}
                                            parse={(e) => validateChange(e)}
                                        />
                                    </div>

                                    <div className="col-12 col-md-6 form-group">
                                        <label>Archivo de aceptación</label>
                                        <div>
                                            {item.acceptance_file ? (
                                                <a
                                                    href={
                                                        item.acceptance_file &&
                                                        item.acceptance_file
                                                    }
                                                    target="_blank"
                                                >
                                                    {item.acceptance_file.replace(
                                                        "/media/docs/",
                                                        ""
                                                    )}
                                                </a>
                                            ) : (
                                                <h5>Sin Archivo de aceptación</h5>
                                            )}
                                        </div>
                                    </div>
                                </React.Fragment>
                            )
                        ) : (
                            ""
                        )}
                    </div>
                </div>
                {renderAccordionHeader("Cursos Aprobados", "courses", coursesRef)}
                <div
                    style={{
                        maxHeight: sections.courses ? '2000px' : '0',
                        overflow: sections.courses ? 'visible' : 'hidden',
                        transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out',
                        opacity: sections.courses ? 1 : 0
                    }}
                >
                    <div className="border p-3 mb-3 rounded">
                        <FieldArray
                            name="other"
                            component={ApprovedCourses}
                            isView={isView}
                            list={list}
                            updateList={updateList}
                        />
                    </div>
                </div>

                {!isView && !isUpdate && (
                    <React.Fragment>
                        {/* Password auto-generated by backend and displayed in alert */}
                    </React.Fragment>
                )}
                {isView && (
                    <div className="col-12 col-md-6 form-group">
                        <label>Codigo QR</label>
                        {item.qr_code ? (
                            <a
                                href={item.qr_code && item.qr_code}
                                target="_blank"
                            >
                                <img
                                    className="qr-mobile"
                                    src={item.qr_code ? item.qr_code : null}
                                    alt={
                                        item.qr_code
                                            ? item.username
                                            : "code_qr.png"
                                    }
                                    disabled={isView}
                                />
                            </a>
                        ) : (
                            <h5>Sin codigo QR</h5>
                        )}
                    </div>
                )}

                <div className="col-12 d-flex flex-column flex-md-row justify-content-center">
                    <div className="flex-3 d-flex flex-column flex-md-row justify-content-end">
                        {item.status === 1 && me.role === ROLE_ADMIN && (
                            <button
                                type="button"
                                onClick={() =>
                                    props.validateUserCreated(item.id)
                                }
                                className="btn btn-success align-self-center px-3 py-3 m-2 font-weight-bold w-100 w-md-auto"
                            >
                                Validar usuario
                            </button>
                        )}
                    </div>
                </div>

                {/* Sección de Permisos Personalizados */}
                {!isCreate && (
                    <CustomPermissionsSection
                        customPermissions={customPermissions}
                        basePermissions={basePermissions}
                        onChange={setCustomPermissions}
                        isView={isView}
                    />
                )}

                <div className="d-flex flex-wrap justify-content-center mt-4">
                    <div className="col-12 col-md-6 d-flex flex-column justify-content-center align-items-center">
                        <Link
                            to={
                                isView
                                    ? "/users"
                                    : isUpdate
                                        ? `/users/${item.id}`
                                        : "/users"
                            }
                            className={`btn btn-danger px-3 py-3 mb-2 mt-2 w-100 ${props.loader ? 'disabled' : ''}`}
                        >
                            {isView ? "Atras" : "Cancelar"}
                        </Link>
                        {!isView && (
                            <button
                                type="submit"
                                disabled={props.loader}
                                className="btn btn-success align-self-center px-3 py-3 m-2 font-weight-bold w-100"
                            >
                                {props.loader ? "Cargando..." : "Guardar"}
                            </button>
                        )}
                    </div>
                </div>
            </LoadMask>
        </form>
    );
};

export const matchPassword = (passe, confirm) =>
    validatorFromFunction(() => {
        return passe === confirm;
    });
export default reduxForm({
    form: "userForm",
    validate: (data) => {
        return validate(data, {
            username: validators.exists()("Campo requerido"),
            first_name: validators.exists()("Campo requerido"),
            last_name: validators.exists()("Campo requerido"),
            email: validators.exists()("Campo requerido"),
            phone_number: validators.exists()("Campo requerido"),
            station: validators.exists()("Campo requerido"),
            no_registry: (val, data) => {
                if (data && data.user_range !== 0) {
                    return combine(validators.exists()("Campo requerido"), validateNumber)(val);
                }
            },
            no_dpi: validators.exists()("Campo requerido"),
            address: validators.exists()("Campo requerido"),
            no_license: validators.exists()("Campo requerido"),
            type_license: validators.exists()("Campo requerido"),
            role: validators.exists()("Campo requerido"),
            user_range: (val) => {
                if (val === undefined || val === null || val === "") {
                    return "Campo requerido";
                }
            },
            job_management: validators.exists()("Campo requerido"),
        });
    },
})(Form);
